import React, { useEffect, useState } from "react";
import Icon from "../../../components/Icon";
import { useNavigate } from "react-router-dom";
import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Button from "../../../components/Button";
import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { hasPermission, hasPlanFeature } from "../../../services/auth";
import { convertIntToMoney, formatCurrencyShort } from "../../../helpers/conversions";
import PipelineMembersModal from "./PipelineMembersModal";
import PipelineDeleteModal from "./PipelineDeleteModal";
import PipelineMembersAvatars from "../../../components/PipelineMembersAvatars";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    useSortable,
    rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Cor única de acento para todas as pipelines (azul institucional da Star).
const ACCENT = '#3D5980';

// Quantas etapas o card lista antes de resumir o resto em "+N".
const MAX_STAGE_ROWS = 4;

// Largura de cada barra de etapa, relativa à etapa mais cheia. Com tudo zerado
// não há o que comparar: devolve 0 e as barras somem, em vez de ficarem todas
// cheias sugerindo volume que não existe.
const stageShares = stages => {
    const counts = stages.map(stage => Number(stage.open_cards) || 0);
    const max = Math.max(...counts, 0);
    return counts.map(count => (max > 0 ? Math.round((count / max) * 100) : 0));
};

// Cartão de pipeline (apresentacional). Quando `dragHandleProps` é fornecido,
// exibe a alça de arraste para reordenar; `innerRef`/`style` vêm do @dnd-kit.
const PipelineCard = ({
    pipeline,
    innerRef,
    style,
    isDragging,
    dragHandleProps,
    openMenuId,
    setOpenMenuId,
    onOpen,
    onEdit,
    onMembers,
    onDelete,
}) => {
    const stages = pipeline.stages ?? [];
    const openCards = pipeline.open_cards ?? 0;
    const openValue = pipeline.open_value ?? 0;
    const automations = pipeline.automations_count ?? 0;
    const shares = stageShares(stages);
    const visibleStages = stages.slice(0, MAX_STAGE_ROWS);
    const hiddenStages = stages.length - visibleStages.length;

    return (
        <div
            className={`pipeline-card-wrap${isDragging ? ' is-dragging' : ''}`}
            ref={innerRef}
            style={style}
        >
            <button
                type="button"
                className="pipeline-card"
                onClick={() => onOpen(pipeline.id)}
            >
                <span className="pipeline-card__head">
                    {pipeline.icon &&
                        <span className="pipeline-card__emoji" aria-hidden="true">{pipeline.icon}</span>
                    }
                    <span className="pipeline-card__name">{pipeline.name}</span>
                </span>

                <span className="pipeline-card__figure">
                    <strong title={convertIntToMoney(openValue, true)}>{formatCurrencyShort(openValue)}</strong>
                    <span className="pipeline-card__figure-label">
                        {openCards.toLocaleString('pt-BR')} card{openCards === 1 ? '' : 's'} em aberto
                    </span>
                </span>

                {stages.length > 0 &&
                    <span className="pipeline-card__stages">
                        {visibleStages.map((stage, index) => {
                            const count = Number(stage.open_cards) || 0;
                            return (
                                <span className="pipeline-card__stage" key={stage.id ?? index}>
                                    <span className="pipeline-card__stage-name">{stage.name}</span>
                                    <span className="pipeline-card__stage-track">
                                        <span
                                            className="pipeline-card__stage-fill"
                                            style={{ width: `${shares[index]}%` }}
                                        />
                                    </span>
                                    <span className="pipeline-card__stage-count">{count.toLocaleString('pt-BR')}</span>
                                </span>
                            );
                        })}
                        {hiddenStages > 0 &&
                            <span className="pipeline-card__stages-more">
                                + {hiddenStages} etapa{hiddenStages === 1 ? '' : 's'}
                            </span>
                        }
                    </span>
                }

                <span className="pipeline-card__foot">
                    <PipelineMembersAvatars members={pipeline.members} />
                    {automations > 0 &&
                        <span className="pipeline-card__automations">
                            <Icon name="zap" size={13} />
                            {automations} automa{automations === 1 ? 'ção' : 'ções'}
                        </span>
                    }
                </span>
            </button>

            {dragHandleProps &&
                <span
                    className="pipeline-card__drag drag"
                    title="Arraste para reordenar"
                    aria-label="Arraste para reordenar a pipeline"
                    {...dragHandleProps}
                >
                    <Icon name="drag" size={16} />
                </span>
            }

            <div className="pipeline-card__menu">
                <button
                    type="button"
                    className="pipeline-card__menu-btn"
                    aria-label="Opções da pipeline"
                    aria-haspopup="true"
                    onClick={() => setOpenMenuId(openMenuId === pipeline.id ? null : pipeline.id)}
                >
                    <Icon name="more-vertical" size={18} />
                </button>
                {openMenuId === pipeline.id &&
                    <>
                        <div className="pipeline-card__menu-overlay" onClick={() => setOpenMenuId(null)} />
                        <div className="pipeline-card__menu-list" role="menu">
                            <button type="button" className="pipeline-card__menu-item" onClick={() => onEdit(pipeline)}>
                                Editar pipeline
                            </button>
                            <button type="button" className="pipeline-card__menu-item" onClick={() => onMembers(pipeline)}>
                                Usuários com acesso
                            </button>
                            <button type="button" className="pipeline-card__menu-item pipeline-card__menu-item--danger" onClick={() => onDelete(pipeline)}>
                                Excluir
                            </button>
                        </div>
                    </>
                }
            </div>
        </div>
    );
};

// Envolve o cartão com o @dnd-kit para reordenar por arrasta e solta. O arraste
// só inicia pela alça (a listeners/attributes vão só nela), preservando o clique
// no corpo do cartão.
const SortablePipelineCard = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.pipeline.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 5 : undefined,
    };

    return (
        <PipelineCard
            {...props}
            innerRef={setNodeRef}
            style={style}
            isDragging={isDragging}
            dragHandleProps={{ ...attributes, ...listeners }}
        />
    );
};

export const PipelinesList = () => {

    const navigate = useNavigate();

    const [pipelines, setPipelines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [membersTarget, setMembersTarget] = useState(null);

    const sensors = [useSensor(PointerSensor, { activationConstraint: { distance: 5 } })];

    useEffect(() => {
        getPipelines();
    }, []);

    const getPipelines = () => {
        setLoading(true);
        api.get(`/pipeline`).then(res => {
            setPipelines(res.data.resources ?? []);
        }).catch(error => {
            renderError(error);
        }).then(() => setLoading(false));
    };

    const openPipeline = id => navigate(`/crm/pipeline/${id}`);
    const createPipeline = () => navigate(`/crm/pipeline/nova`);

    // Abre a página de edição da pipeline (/crm/pipeline/:id/editar).
    const editPipeline = pipeline => {
        setOpenMenuId(null);
        navigate(`/crm/pipeline/${pipeline.id}/editar`);
    };

    const openMembers = pipeline => {
        setOpenMenuId(null);
        setMembersTarget(pipeline);
    };

    const askDelete = pipeline => {
        setOpenMenuId(null);
        setDeleteTarget(pipeline);
    };

    // Reordena as pipelines por arrasta e solta e persiste a nova ordem. Atualiza
    // otimista e reverte se o backend falhar.
    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = pipelines.findIndex(p => p.id === active.id);
        const newIndex = pipelines.findIndex(p => p.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;

        const previous = pipelines;
        const reordered = arrayMove(pipelines, oldIndex, newIndex);
        setPipelines(reordered);

        api.put(`/pipeline/reorder`, { ids: reordered.map(p => p.id) })
            .catch(error => {
                setPipelines(previous);
                renderError(error);
            });
    };

    const cardHandlers = {
        openMenuId,
        setOpenMenuId,
        onOpen: openPipeline,
        onEdit: editPipeline,
        onMembers: openMembers,
        onDelete: askDelete,
    };

    return (
        <PanelTemplate id="crm-pipelines">
            <PageHeader
                left={<h1 className="pipelines-title">Pipelines</h1>}
                right={
                    <>
                        {hasPlanFeature('crm') && hasPermission('edit_crm_settings') &&
                            <Button
                                type={`secondary`}
                                size={`small`}
                                svg={`zap`}
                                btnClass={`pipelines-config-btn`}
                                text={`Automações`}
                                link={`/crm/automacoes`}
                            />
                        }
                        {pipelines.length > 0 &&
                            <Button
                                type={`primary`}
                                size={`small`}
                                svg={`plus-white`}
                                text={`Criar pipeline`}
                                action={createPipeline}
                            />
                        }
                    </>
                }
            />

            {loading &&
                <div className="pipelines-grid">
                    {[0, 1, 2, 3].map(i => (
                        <div key={i} className="pipeline-card pipeline-card--skeleton" />
                    ))}
                </div>
            }

            {!loading && pipelines.length === 0 &&
                <div className="pipelines-empty">
                    <h2>Crie sua primeira pipeline</h2>
                    <p>Você ainda não criou nenhuma pipeline. Crie uma pipeline para começar a organizar suas operações.</p>
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`plus-white`}
                        text={`Criar pipeline`}
                        action={createPipeline}
                    />
                </div>
            }

            {!loading && pipelines.length > 0 &&
                <>
                    <div className="pipelines-grid">
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={pipelines.map(p => p.id)} strategy={rectSortingStrategy}>
                                {pipelines.map(pipeline => (
                                    <SortablePipelineCard key={pipeline.id} pipeline={pipeline} {...cardHandlers} />
                                ))}
                            </SortableContext>
                        </DndContext>

                        <button
                            type="button"
                            className="pipeline-card pipeline-card--new"
                            onClick={createPipeline}
                        >
                            <span className="pipeline-card--new__plus">+</span>
                            <span>Criar pipeline</span>
                        </button>
                    </div>
                </>
            }

            {deleteTarget &&
                <PipelineDeleteModal
                    pipeline={deleteTarget}
                    close={() => setDeleteTarget(null)}
                    onDeleted={() => {
                        setDeleteTarget(null);
                        getPipelines();
                    }}
                />
            }

            {membersTarget &&
                <PipelineMembersModal
                    pipeline={membersTarget}
                    close={saved => {
                        setMembersTarget(null);
                        if (saved) getPipelines();
                    }}
                />
            }
        </PanelTemplate>
    );
};

export default PipelinesList;
